import { useEffect, useState } from 'react';
import { exportIdentityMnemonicPhrase } from '../../p2p/identity.js';
import BlurredRecoveryPhrase from '../../ui/BlurredRecoveryPhrase.jsx';

export default function RecoveryPhraseModal({ open, onConfirm }) {
  const [phrase, setPhrase] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [savedConfirmed, setSavedConfirmed] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setSavedConfirmed(false);
    setNotice('');
    setError('');
    setBusy(true);

    exportIdentityMnemonicPhrase()
      .then((nextPhrase) => {
        setPhrase(nextPhrase);
      })
      .catch((loadError) => {
        setError(loadError.message);
      })
      .finally(() => {
        setBusy(false);
      });
  }, [open]);

  if (!open) {
    return null;
  }

  async function copyPhrase() {
    if (!phrase) {
      return;
    }
    await navigator.clipboard.writeText(phrase);
    setNotice('Recovery phrase copied. Paste it into a password manager or write it on paper.');
  }

  return (
    <div className="modal-backdrop">
      <div
        className="modal-dialog recovery-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="recovery-modal-title"
      >
        <p className="eyebrow">Before your first game</p>
        <h2 id="recovery-modal-title">Save your recovery phrase</h2>
        <p className="recovery-lede">
          deChess stores your player identity only in this browser. Your
          48-word recovery phrase is the only way to bring your player ID,
          ratings, and certificates to another device.
        </p>

        <ul className="recovery-guidance">
          <li>
            <strong>Without it, you cannot recover your identity.</strong>
            Clearing browser data or switching devices without a backup means
            permanent loss of this player ID.
          </li>
          <li>
            <strong>Treat it like a password.</strong>
            Anyone with these words can control your identity. Do not share them
            or store them in public chats or cloud notes you do not trust.
          </li>
          <li>
            <strong>How to save it:</strong>
            copy the phrase below, write all 48 words in order on paper, or
            store them in a password manager. Check the words carefully — one
            typo breaks recovery.
          </li>
          <li>
            You can always reveal the phrase again under Account &amp; network.
          </li>
        </ul>

        {error ? (
          <p className="error" role="alert">{error}</p>
        ) : (
          <>
            <BlurredRecoveryPhrase value={phrase} busy={busy} />
            <div className="identity-actions">
              <button
                type="button"
                onClick={copyPhrase}
                disabled={busy || !phrase}
              >
                Copy phrase
              </button>
            </div>
          </>
        )}

        {notice && <p className="notice">{notice}</p>}

        <label className="recovery-confirm" htmlFor="recovery-saved-confirm">
          <input
            id="recovery-saved-confirm"
            name="recovery-saved-confirm"
            type="checkbox"
            checked={savedConfirmed}
            onChange={(event) => setSavedConfirmed(event.target.checked)}
            disabled={busy || Boolean(error)}
          />
          <span>
            I have saved my recovery phrase in a safe place and understand that
            deChess cannot restore my identity without it.
          </span>
        </label>

        <button
          type="button"
          className="large-cta recovery-continue"
          onClick={onConfirm}
          disabled={!savedConfirmed || busy || Boolean(error)}
        >
          Continue to game
        </button>
      </div>
    </div>
  );
}
